import { WifiOff } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

/**
 * Shown when we hold a session but couldn't reach the server to verify it
 * (Issue #1360).
 *
 * This state used to have no screen at all. `/api/auth/me` had no time limit,
 * so a request that STALLED — a tower hand-off, a dead spot, a wedged service
 * worker — never settled, `loading` never cleared, and `RequireAuth` spun
 * forever with no error, no retry, and no way out but clearing site data. A
 * tech in the field is never going to do that.
 *
 * Two things this screen deliberately does NOT do:
 *   · it does not bounce to /login — the session may well be fine, and saying
 *     "you're signed out" when we simply couldn't ask is a lie that costs the
 *     tech his place in the job he was working;
 *   · it does not clear the token — `auth.tsx` keeps it precisely so "Try
 *     again" can succeed the moment signal comes back.
 */
export function AuthUnreachable({ onRetry }: { onRetry: () => void }) {
    const { loading, logout } = useAuth();

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background p-6">
            <Card className="w-full max-w-md text-center shadow-lg">
                <CardContent className="space-y-5 px-8 py-10">
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-warning/10">
                        <WifiOff className="h-7 w-7 text-warning" />
                    </div>
                    <div>
                        <h1 className="text-xl font-black tracking-tight">Can&apos;t reach Groundwork Command</h1>
                        <p className="mt-2 text-sm text-foreground/70">
                            You&apos;re still signed in — we just couldn&apos;t check in with the server. This is
                            usually signal. Try again in a moment.
                        </p>
                    </div>
                    <Button size="lg" className="w-full" disabled={loading} onClick={onRetry}>
                        {loading ? "Trying…" : "Try again"}
                    </Button>
                    <p className="text-xs text-muted-foreground">
                        Still stuck?{" "}
                        <button className="underline hover:text-foreground" onClick={() => logout()}>
                            Sign in again
                        </button>
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}
